import React, { useState } from "react";
import { Link } from "react-router-dom";
import MaterialIcon from "../MaterialIcon";
import { ICON_TONE_CLASSES } from "../MetricStatCard";

const PANEL_CLASS =
  "tonal-card rounded-2xl border border-outline-variant/30 bg-surface-container-lowest";

function formatMetricValue(value, locale) {
  if (value === "—" || value == null) return "—";
  if (typeof value === "string") return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(locale === "de" ? "de-DE" : "en-US");
}

function MetricIconBadge({ icon, tone = "primary", size = "md" }) {
  if (!icon) return null;
  const toneClass = ICON_TONE_CLASSES[tone] ?? ICON_TONE_CLASSES.primary;
  const box = size === "lg" ? "h-12 w-12" : "h-10 w-10";
  const iconSize = size === "lg" ? "text-[24px]" : "text-[20px]";
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-xl ${box} ${toneClass}`}
      aria-hidden
    >
      <MaterialIcon name={icon} className={iconSize} />
    </div>
  );
}

export function DashboardHeroMetric({
  label,
  value,
  footnote,
  trendLabel,
  trendIcon = "trending_up",
  icon,
  iconTone = "primary",
  borderClass = "border-primary",
  accent = false,
  loading = false,
  onClick,
  className = "",
}) {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={[
        PANEL_CLASS,
        "border-l-4 p-stack-lg text-left",
        borderClass,
        accent ? "bg-secondary-container/10" : "",
        onClick ? "cursor-pointer transition-all hover:shadow-active" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <span className="font-label-caps text-label-caps uppercase tracking-widest text-on-surface-variant">
            {label}
          </span>
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <h2
              className={`font-headline-xl text-headline-xl tabular-nums ${
                accent ? "text-secondary" : "text-primary"
              }`}
            >
              {loading ? (
                <span className="inline-block h-9 w-20 animate-pulse rounded-lg bg-surface-container-high" />
              ) : (
                value
              )}
            </h2>
            {trendLabel && !loading ? (
              <span className="mb-1.5 inline-flex items-center gap-1 font-label-sm text-label-sm text-secondary">
                <MaterialIcon name={trendIcon} className="text-[14px]" />
                {trendLabel}
              </span>
            ) : null}
          </div>
        </div>
        {icon ? <MetricIconBadge icon={icon} tone={iconTone} size="lg" /> : null}
      </div>
      {footnote ? (
        <div className="mt-4 border-t border-outline-variant/10 pt-4">
          <p className="font-label-sm text-label-sm text-on-surface-variant">{footnote}</p>
        </div>
      ) : null}
    </Component>
  );
}

export function DashboardMiniStat({
  label,
  value,
  locale = "en",
  loading = false,
  highlight = false,
  errorTone = false,
  dot = false,
  icon,
  iconTone,
  onClick,
}) {
  const Component = onClick ? "button" : "div";
  const resolvedTone = iconTone ?? (errorTone ? "error" : highlight ? "secondary" : "primary");

  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={[
        "rounded-xl border border-outline-variant/30 p-4 text-left transition-all",
        highlight ? "bg-secondary/5 ring-1 ring-secondary/10" : "bg-surface-container-lowest",
        onClick ? "cursor-pointer hover:border-primary/25 hover:shadow-soft" : "",
      ].join(" ")}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {icon ? <MetricIconBadge icon={icon} tone={resolvedTone} size="sm" /> : null}
          <p
            className={`font-label-sm text-label-sm leading-snug ${
              errorTone ? "text-error" : highlight ? "text-secondary" : "text-on-surface-variant"
            }`}
          >
            {label}
          </p>
        </div>
        {dot ? <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-secondary" aria-hidden /> : null}
      </div>
      <p
        className={`font-headline-lg text-headline-lg tabular-nums ${
          errorTone ? "text-error" : highlight ? "text-secondary" : "text-primary"
        }`}
      >
        {loading ? "—" : formatMetricValue(value, locale)}
      </p>
    </Component>
  );
}

export function DashboardProgressRow({ label, value, percent, barClassName = "bg-primary", icon }) {
  const safePercent = Math.min(100, Math.max(0, percent ?? 0));
  return (
    <div className="rounded-xl border border-outline-variant/40 bg-surface-bright p-stack-md">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 font-label-sm text-label-sm text-on-surface-variant">
          {icon ? <MaterialIcon name={icon} className="text-[16px] text-secondary" /> : null}
          {label}
        </span>
        <span className="font-headline-md text-headline-md tabular-nums text-primary">{value}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-container-high">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barClassName}`}
          style={{ width: `${safePercent}%` }}
        />
      </div>
    </div>
  );
}

export function DashboardAlertBanner({ icon, title, message, variant = "secondary" }) {
  const styles =
    variant === "warning"
      ? "border border-yellow-500/25 bg-yellow-500/10"
      : "border border-secondary/20 bg-secondary/5";
  const iconStyles =
    variant === "warning"
      ? "bg-yellow-500/15 text-yellow-700"
      : "bg-secondary text-on-primary";

  return (
    <div className={`mt-stack-lg flex items-start gap-4 rounded-xl p-4 ${styles}`}>
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconStyles}`}
      >
        <MaterialIcon name={icon} className="text-[20px]" />
      </div>
      <div className="min-w-0">
        <p className="font-label-sm text-label-sm font-semibold text-primary">{title}</p>
        <p className="mt-0.5 font-body-md text-body-md text-on-surface-variant">{message}</p>
      </div>
    </div>
  );
}

const TREND_CHART_BAR_AREA_PX = 132;

function TrendBarColumn({ bar, locale = "en" }) {
  const [hovered, setHovered] = useState(false);
  const barHeightPx = Math.max(6, Math.round((bar.height / 100) * TREND_CHART_BAR_AREA_PX));
  const formattedValue = formatMetricValue(bar.value, locale);
  const showPercent = typeof bar.percent === "number" && bar.total > 0;

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div
        className="relative flex items-end"
        style={{ height: `${TREND_CHART_BAR_AREA_PX}px` }}
      >
        <div
          className={[
            "pointer-events-none absolute left-1/2 z-10 min-w-[56px] -translate-x-1/2 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-center shadow-[0_10px_28px_rgba(15,23,42,0.14)] transition-all duration-200",
            hovered ? "visible translate-y-0 opacity-100" : "invisible translate-y-1 opacity-0",
          ].join(" ")}
          style={{ bottom: `${barHeightPx + 10}px` }}
          aria-hidden={!hovered}
        >
          <p className="truncate font-label-sm text-[10px] font-medium text-on-surface-variant">
            {bar.label}
          </p>
          <p className="font-headline-md text-base font-bold tabular-nums leading-tight text-primary">
            {formattedValue}
          </p>
          {showPercent ? (
            <p className="mt-0.5 font-label-sm text-[10px] tabular-nums text-secondary">
              {bar.percent}%
            </p>
          ) : null}
        </div>

        <button
          type="button"
          className="group/bar relative w-full rounded-t-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
          style={{ height: `${barHeightPx}px` }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setHovered(true)}
          onBlur={() => setHovered(false)}
          aria-label={`${bar.label}: ${formattedValue}${showPercent ? ` (${bar.percent}%)` : ""}`}
        >
          <div
            className={[
              "h-full w-full rounded-t-md transition-all duration-200 ease-out",
              bar.className ?? "bg-surface-container-high",
              hovered
                ? "scale-[1.04] shadow-[0_8px_20px_rgba(15,23,42,0.12)] brightness-110"
                : "opacity-90 group-hover/bar:opacity-100",
            ].join(" ")}
          />
        </button>
      </div>
      <span
        className={[
          "mt-2 block h-4 w-full truncate text-center text-[10px] font-medium leading-4 transition-colors duration-200",
          hovered ? "font-semibold text-primary" : "text-on-surface-variant",
        ].join(" ")}
      >
        {bar.label}
      </span>
    </div>
  );
}

export function DashboardTrendBars({
  title,
  bars = [],
  emptyMessage,
  loading = false,
  locale = "en",
}) {
  return (
    <div className="rounded-xl border border-outline-variant/30 bg-surface-bright p-stack-md">
      <h4 className="mb-4 flex items-center gap-2 font-label-caps text-label-caps uppercase tracking-widest text-on-surface-variant">
        <MaterialIcon name="analytics" className="text-[16px] text-secondary" />
        {title}
      </h4>
      {loading ? (
        <div className="flex w-full gap-2 px-1">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="flex min-w-0 flex-1 flex-col">
              <div
                className="flex items-end"
                style={{ height: `${TREND_CHART_BAR_AREA_PX}px` }}
              >
                <div className="h-[40%] w-full animate-pulse rounded-t-md bg-surface-container-high" />
              </div>
              <div className="mx-auto mt-2 h-2 w-10 animate-pulse rounded bg-surface-container-high" />
            </div>
          ))}
        </div>
      ) : !bars.length ? (
        <p
          className="flex items-center justify-center px-4 text-center font-body-md text-on-surface-variant"
          style={{ minHeight: `${TREND_CHART_BAR_AREA_PX + 24}px` }}
        >
          {emptyMessage || "—"}
        </p>
      ) : (
        <div className="flex w-full gap-2 px-1">
          {bars.map((bar, index) => (
            <TrendBarColumn key={bar.key ?? index} bar={bar} locale={locale} />
          ))}
        </div>
      )}
    </div>
  );
}

export function DashboardQuickAction({ to, icon, title, description, iconTone = "primary", onClick }) {
  const className = [
    "group flex items-center gap-4 rounded-2xl border border-outline-variant/30 p-stack-md text-left",
    PANEL_CLASS,
    "transition-all hover:border-primary/20 hover:shadow-active",
  ].join(" ");

  const inner = (
    <>
      <MetricIconBadge icon={icon} tone={iconTone} size="lg" />
      <div className="min-w-0 flex-1">
        <p className="font-headline-md text-sm font-semibold text-primary">{title}</p>
        <p className="mt-0.5 font-label-sm text-label-sm text-on-surface-variant">{description}</p>
      </div>
      <MaterialIcon
        name="arrow_forward"
        className="shrink-0 text-[18px] text-on-surface-variant transition-transform group-hover:translate-x-0.5 group-hover:text-secondary"
      />
    </>
  );

  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
}

export function DashboardResourceCard({
  title,
  description,
  links = [],
  icon = "menu_book",
  heroClassName = "",
}) {
  return (
    <div className={`${PANEL_CLASS} overflow-hidden`}>
      <div
        className={[
          "architect-gradient relative flex h-36 items-end overflow-hidden p-stack-md",
          heroClassName,
        ].join(" ")}
      >
        <div className="pointer-events-none absolute -right-4 -top-4 opacity-[0.12]">
          <MaterialIcon name={icon} className="text-[120px] text-white" />
        </div>
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white backdrop-blur-sm">
            <MaterialIcon name={icon} className="text-[22px]" />
          </div>
          <p className="font-headline-md text-headline-md text-white">{title}</p>
        </div>
      </div>
      <div className="p-stack-md">
        {description ? (
          <p className="mb-4 font-body-md text-body-md leading-relaxed text-on-surface-variant">
            {description}
          </p>
        ) : null}
        <ul className="space-y-2.5">
          {links.map((link) => (
            <li key={link.to + link.label}>
              <Link
                to={link.to}
                className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 font-label-sm text-label-sm text-primary transition-colors hover:bg-surface-container-low"
              >
                <MaterialIcon
                  name={link.icon || "chevron_right"}
                  className="text-[16px] text-secondary"
                />
                <span className="group-hover:underline">{link.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function DashboardPanel({ title, badge, icon, children, className = "" }) {
  return (
    <div className={`${PANEL_CLASS} p-stack-lg ${className}`}>
      <div className="mb-stack-lg flex items-center justify-between gap-3">
        <h3 className="inline-flex items-center gap-2.5 font-headline-md text-headline-md text-primary">
          {icon ? (
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/5 text-primary">
              <MaterialIcon name={icon} className="text-[18px]" />
            </span>
          ) : null}
          {title}
        </h3>
        {badge ? (
          <span className="rounded-full bg-surface-container-high px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
            {badge}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export function DashboardPageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <section className="mb-stack-lg flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        {eyebrow ? (
          <p className="mb-2 font-label-caps text-label-caps uppercase tracking-widest text-secondary">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="font-headline-xl text-headline-xl text-primary">{title}</h1>
        {subtitle ? (
          <p className="mt-2 max-w-2xl font-body-lg text-body-lg text-on-surface-variant">
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
    </section>
  );
}

/** Normalize stat values into bar heights (20–100%) for trend visualization. */
export function buildTrendBarsFromValues(items) {
  if (!items?.length) return [];
  const values = items.map((item) => Number(item.value) || 0);
  const max = Math.max(...values, 1);
  const total = values.reduce((sum, value) => sum + value, 0);

  return items.map((item, index) => {
    const raw = values[index];
    const height = raw === 0 ? 12 : 20 + Math.round((raw / max) * 80);
    const percent = total > 0 ? Math.round((raw / total) * 100) : 0;

    return {
      key: item.key,
      label: item.label,
      value: raw,
      total,
      percent,
      height,
      className: item.className,
    };
  });
}

export function normalizeSentimentDistribution(raw) {
  if (!raw) return null;
  return {
    positive: raw.positive ?? raw.positive_count ?? 0,
    neutral: raw.neutral ?? raw.neutral_count ?? 0,
    negative: raw.negative ?? raw.negative_count ?? 0,
    positivePercent: raw.positive_percent ?? raw.positivePercent ?? 0,
    neutralPercent: raw.neutral_percent ?? raw.neutralPercent ?? 0,
    negativePercent: raw.negative_percent ?? raw.negativePercent ?? 0,
  };
}

export function buildSentimentTrendBars(sentiment, t) {
  const normalized = normalizeSentimentDistribution(sentiment);
  if (!normalized) return [];
  return buildTrendBarsFromValues([
    {
      key: "positive",
      label: t("home.positive"),
      value: normalized.positive,
      className: "bg-secondary",
    },
    {
      key: "neutral",
      label: t("home.neutral"),
      value: normalized.neutral,
      className: "bg-surface-container-high",
    },
    {
      key: "negative",
      label: t("home.negative"),
      value: normalized.negative,
      className: "bg-error",
    },
  ]);
}

export function buildCaseDistributionBars(caseStats, t) {
  if (!caseStats) return [];
  return buildTrendBarsFromValues([
    {
      key: "pending",
      label: t("adminStats.pending"),
      value: caseStats.pending,
      className: "bg-surface-container-high",
    },
    {
      key: "progress",
      label: t("adminStats.inProgress"),
      value: caseStats.inProgress,
      className: "bg-secondary/70",
    },
    {
      key: "done",
      label: t("adminStats.completed"),
      value: caseStats.completed,
      className: "bg-primary",
    },
    {
      key: "rejected",
      label: t("adminStats.rejected"),
      value: caseStats.rejected,
      className: "bg-outline",
    },
  ]);
}

export { formatMetricValue, PANEL_CLASS };
